/**
 * Manufacturer-specific waypoint capture binding implementations
 *
 * This file contains the declarative bindings (subscriptions, event listeners) for waypoint capture.
 * Each manufacturer has its own implementation that may use completely different protocols/approaches.
 */

import { MutableRefObject, useMemo, useRef } from 'react';
import { useDigitalOutputBinding } from '../../../../api/hooks/binding/digital-output.binding';
import {
  RealtimePositionEvent,
  RealtimePositionResult,
  useRealtimePositionMonitorBinding,
} from '../../../../api/hooks/binding/realtime-position.binding';
import { useScriptActionBinding } from '../../../../api/hooks/binding/script-action.binding';
import { useSetToolConfigBinding } from '../../../../api/hooks/binding/tool-config.binding';
import {
  TouchableInputResult,
  useTouchableInputBinding,
} from '../../../../api/hooks/binding/touchable-input.binding';
import { ProgramState } from '../../../../components/machine/machine-state';
import { isLocalDev } from '../../../../config/config';
import { useRobotManufacturerContext } from '../../../../pages/machine/robot-manufacturer-context';
import { robotRegistryService } from '../../../../robot/robot-registry.service';
import { RobotManufacturer } from '../../../../robot/robot.types';
import { hasBeaconEngineSupport } from '../../../../robot/robot.utils';
import { logger } from '../../../../util/logger';
import { TeachIOMapping, ToolConfig } from '../model/flow.types';

export interface WaypointCaptureBindingsConfig {
  machineId: string;
  ioMapping: TeachIOMapping;
  toolConfig: ToolConfig;
  delayPress?: number;
  delayLongPress?: number;
  onPress?: (position: RealtimePositionEvent | null) => void;
  onLongPress?: () => void;
  innerRef?: MutableRefObject<WaypointCaptureBindingsRef | undefined>;
}

export interface WaypointCaptureBindingsRef {
  realtimePosition: RealtimePositionResult | undefined;
  forcePress: () => void;
  forceLongPress: () => void;
}

/**
 * Universal Robots waypoint capture bindings implementation
 * Uses UR-specific digital IO, script actions, and realtime position monitoring
 */
const useURWaypointCaptureBindings = (
  config: WaypointCaptureBindingsConfig,
  options?: { skip?: boolean }
): void => {
  const {
    machineId,
    ioMapping,
    toolConfig,
    delayPress = 100,
    delayLongPress = 1200,
    innerRef,
  } = config;

  const skip = options?.skip;

  const realtimePosition = useRef<RealtimePositionResult | undefined>();

  const onPress = (result?: TouchableInputResult) => {
    if (isLocalDev()) {
      // Store original last position and leverage its data if we can, otherwise
      // we will return fake data
      const originalLastPosition =
        realtimePosition.current?.getLastPosition ?? (() => undefined);

      realtimePosition.current = {
        getLastPosition: () => {
          const originalData = originalLastPosition();

          return (
            originalData ?? {
              jointPositions: [0, 0, 0, 0, 0, 0],
              waypoint: {
                rx: 0,
                ry: 0,
                rz: 0,
                x: 0,
                y: 0,
                z: 0,
              },
            }
          );
        },
      };
    }

    let position: RealtimePositionEvent | null | undefined;

    if (result?.metadata?.pose && result.metadata?.qnear) {
      position = {
        jointPositions: result.metadata.qnear,
        waypoint: result.metadata.pose,
      };
    } else {
      position = realtimePosition.current?.getLastPosition();
    }

    if (position) {
      if (typeof config.onPress === 'function') {
        config.onPress(position);
      }
    }
  };

  // Setup digital output for teach light (pulsing)
  useDigitalOutputBinding(
    machineId,
    {
      output: ioMapping.output.light,
      state: true,
      pulse: true,
      pulseInterval: 750,
      finalState: false,
    },
    { skip }
  );

  // Build and send TCP configuration script
  const setTcpScript = useMemo(() => {
    const torchTcp = toolConfig.tcp;
    const torchCog = toolConfig.cog;
    const torchInteria = toolConfig.inertia;
    const torchMass = toolConfig.mass;

    const tcp = `p[${torchTcp.x}, ${torchTcp.y}, ${torchTcp.z}, ${torchTcp.rx}, ${torchTcp.ry}, ${torchTcp.rz}]`;

    const script = [
      `sec set_tcp():`,
      `\tset_tcp(${tcp})`,
      `\tset_target_payload(${torchMass}, [${torchCog.join(
        ','
      )}], [${torchInteria.join(',')}])`,
      `end`,
    ].join('\n');

    return script;
  }, [toolConfig]);

  useScriptActionBinding(
    machineId,
    {
      script: setTcpScript,
      validProgramStates: [
        ProgramState.PROGRAM_STOPPED,
        ProgramState.PROGRAM_PAUSED,
      ],
    },
    { skip }
  );

  // Setup touchable input monitoring (teach pendant button)
  useTouchableInputBinding(
    machineId,
    {
      input: ioMapping.input.capture,
      delayPress,
      delayLongPress,
      onPress,
      onLongPress: config.onLongPress,
    },
    { skip }
  );

  // Setup realtime position monitoring
  realtimePosition.current = useRealtimePositionMonitorBinding(
    machineId,
    {},
    { skip }
  );

  // Set up innerRef for imperative access (AFTER realtimePosition is assigned)
  // Only set innerRef if this hook is active (not skipped)
  if (innerRef && !skip) {
    innerRef.current = {
      forceLongPress: () => {
        if (config.onLongPress) {
          config.onLongPress();
        }
      },
      forcePress: () => {
        if (config.onPress) {
          // Retrieve current position and construct result object if available
          const currentPosition = realtimePosition.current?.getLastPosition();
          if (currentPosition) {
            const result: TouchableInputResult = {
              state: true,
              metadata: {
                pose: currentPosition.waypoint,
                qnear: currentPosition.jointPositions,
              },
            };
            onPress(result);
          } else {
            // Fallback to calling without result (will use getLastPosition in onPress)
            onPress();
          }
        }
      },
      realtimePosition: realtimePosition.current,
    };
  }
};

/**
 * BeaconEngine waypoint capture bindings implementation
 * Uses BeaconEngine-specific tool configuration, digital IO, and realtime position monitoring
 */
const useBeaconEngineWaypointCaptureBindings = (
  config: WaypointCaptureBindingsConfig,
  options?: { skip?: boolean }
): void => {
  const {
    machineId,
    ioMapping,
    toolConfig,
    delayPress = 100,
    delayLongPress = 1200,
    innerRef,
  } = config;

  const skip = options?.skip;

  const realtimePosition = useRef<RealtimePositionResult | undefined>();

  const onPress = (result?: TouchableInputResult) => {
    if (isLocalDev()) {
      // Store original last position and leverage its data if we can, otherwise
      // we will return fake data
      const originalLastPosition =
        realtimePosition.current?.getLastPosition ?? (() => undefined);

      realtimePosition.current = {
        getLastPosition: () => {
          const originalData = originalLastPosition();

          return (
            originalData ?? {
              jointPositions: [0, 0, 0, 0, 0, 0],
              waypoint: {
                rx: 0,
                ry: 0,
                rz: 0,
                x: 0,
                y: 0,
                z: 0,
              },
            }
          );
        },
      };
    }

    let position: RealtimePositionEvent | null | undefined;

    if (result?.metadata?.pose && result.metadata?.qnear) {
      position = {
        jointPositions: result.metadata.qnear,
        waypoint: result.metadata.pose,
      };
    } else {
      position = realtimePosition.current?.getLastPosition();
    }

    if (position) {
      if (typeof config.onPress === 'function') {
        config.onPress(position);
      }
    }
  };

  // Setup digital output for teach light (pulsing)
  useDigitalOutputBinding(
    machineId,
    {
      output: ioMapping.output.light,
      state: true,
      pulse: true,
      pulseInterval: 750,
      finalState: false,
    },
    { skip }
  );

  // Set tool configuration using the new binding
  useSetToolConfigBinding(
    machineId,
    {
      toolConfig: {
        name: toolConfig.name,
        tcp: toolConfig.tcp,
        mass: toolConfig.mass,
        cog: {
          x: toolConfig.cog[0],
          y: toolConfig.cog[1],
          z: toolConfig.cog[2],
        },
        inertia: {
          xx: toolConfig.inertia[0],
          yy: toolConfig.inertia[1],
          zz: toolConfig.inertia[2],
          xy: toolConfig.inertia[3],
          xz: toolConfig.inertia[4],
          yz: toolConfig.inertia[5],
        },
      },
    },
    { skip }
  );

  // Setup touchable input monitoring (teach pendant button)
  useTouchableInputBinding(
    machineId,
    {
      input: ioMapping.input.capture,
      delayPress,
      delayLongPress,
      onPress,
      onLongPress: config.onLongPress,
    },
    { skip }
  );

  // Setup realtime position monitoring
  realtimePosition.current = useRealtimePositionMonitorBinding(
    machineId,
    {},
    { skip }
  );

  // Set up innerRef for imperative access (AFTER realtimePosition is assigned)
  // Only set innerRef if this hook is active (not skipped)
  if (innerRef && !skip) {
    innerRef.current = {
      forceLongPress: () => {
        if (config.onLongPress) {
          config.onLongPress();
        }
      },
      forcePress: () => {
        if (config.onPress) {
          // Retrieve current position and construct result object if available
          const currentPosition = realtimePosition.current?.getLastPosition();
          if (currentPosition) {
            const result: TouchableInputResult = {
              state: true,
              metadata: {
                pose: currentPosition.waypoint,
                qnear: currentPosition.jointPositions,
              },
            };
            onPress(result);
          } else {
            // Fallback to calling without result (will use getLastPosition in onPress)
            onPress();
          }
        }
      },
      realtimePosition: realtimePosition.current,
    };
  }
};

/**
 * Manufacturer-agnostic waypoint capture bindings hook
 * Delegates to manufacturer-specific implementations based on context
 *
 * All manufacturer-specific hooks are always called (following React rules),
 * but with skip=true when the manufacturer doesn't match.
 */
export const useWaypointCaptureBindings = (
  config: WaypointCaptureBindingsConfig
): void => {
  const manufacturerContext = useRobotManufacturerContext();
  const { manufacturer } = manufacturerContext;
  const isBeaconEngine = hasBeaconEngineSupport(manufacturerContext);

  let skipAll = false;

  if (!manufacturer) {
    logger.error(`No manufacturer found in waypoint capture bindings context.`);
    skipAll = true;
  }

  const manufacturerNames = robotRegistryService.getManufacturers();

  // Validate that at least one manufacturer is supported
  if (manufacturer && !manufacturerNames.includes(manufacturer)) {
    logger.error(
      `Unknown manufacturer in waypoint capture bindings: ${manufacturer}`
    );
  }

  // Always call all manufacturer hooks with appropriate skip flag
  const skipUR = skipAll || manufacturer !== RobotManufacturer.UNIVERSAL_ROBOTS;
  useURWaypointCaptureBindings(config, { skip: skipUR });

  const skipBeaconEngine = skipAll || !isBeaconEngine;
  useBeaconEngineWaypointCaptureBindings(config, { skip: skipBeaconEngine });
};
